import { getSrcs, imageUrl } from '@helpers/image';
import { Device, until } from '@helpers/media';
import * as React from 'react';
/**
 * @param image The image model (src, altText, focalPoint if required)
 * @param width The width of the image (required)
 * @param mobileWidth The mobile width of the image (optional - default set to 375)
 * @param height The height of the image (required if width is not provided)
 * @param ratio The aspect ratio of the image (height / width)
 * @param includeSrcSet Whether to include a srcSet (optional - default set to true)
 * @param fetchPriority The fetch priority of the image (high, low, auto)
 * @param loading The loading strategy of the image (lazy, eager)
 *
 * @description !!!! DISCLAIMERS !!!!
 * - The image property must contain a valid image model to render the image.
 * - The width property is required for generating the image URL.
 * - The ratio property is optional and is used to set the aspect ratio of the image, also, the height within the image URL.
 * - If using a motion image `m.img`, please apply the motion to a wrapper element instead of the image itself, and use a regular `<img>`.
 * - The image component does not handle `lazy` loading or `eager` loading by default, so it is up to the user to implement this functionality.
 * - The image component only generates a `srcSet` if the `includeSrcSet` property is set to true. If false, it will generate a 2x version of the image as a fallback.
 * - The image component only generates `sizes` if the `includeSrcSet` property is set to true. If false, it will not generate any sizes.
 * - Both `srcSet` and `sizes` are optional properties. If not provided, the component will generate default values based on the provided image model and dimensions. If not adequate, users can provide their own values and is recommended to do so.
 *
 */
const Image = ({ image, width, mobileWidth, height, ratio, includeSrcSet = true, fetchPriority, loading, ...rest }) => {
    if (!image || !image.src) {
        return null;
    }
    // Ensure width is provided.
    if (!width) {
        throw new Error('Image component requires width to be provided.');
    }
    // Ensure loading is correctly set to lazy or eager.
    if (!loading) {
        throw new Error('Image component requires loading to be correctly set to lazy or eager.');
    }
    // Set default sizes based on mobile width provided.
    const defaultSizes = generateSizes(includeSrcSet, width, mobileWidth);
    // Use provided `sizes` or default sizes.
    const computedSizes = rest.sizes || defaultSizes;
    // Generate the image URL.
    const src = imageUrl(image, { width, height });
    // Generate default srcSet based on the provided image model and dimensions.
    const defaultSrcSet = generateSrcSet(includeSrcSet, image, width, height, ratio);
    // Use provided `srcSet` or default srcSet.
    const computedSrcSet = rest.srcSet || defaultSrcSet;
    return (React.createElement("img", { style: ratio ? { aspectRatio: `${1 / ratio}` } : undefined, fetchPriority: loading === 'eager' && !fetchPriority ? 'high' : fetchPriority, loading: loading, src: src, srcSet: computedSrcSet, sizes: computedSizes, width: width, height: height, alt: image.altText || '', role: !image.altText ? 'presentation' : undefined, ...rest }));
    function generateSizes(includedSrcSet, width, mobileWidth) {
        let srcSetEnabled = includedSrcSet;
        // If difference in widths is minimal, we don't need to generate any sizes.
        if (width - (mobileWidth || 375) < 100) {
            srcSetEnabled = false;
        }
        // If excluded, then we don't need to generate sizes.
        if (!srcSetEnabled) {
            return undefined;
        }
        // If we have a mobile width, we need to set the lower width to that.
        if (mobileWidth) {
            return `${until(Device.MobileLarge)} ${mobileWidth}px, ${until(Device.TabletLarge)} 100vw, ${width}px`;
        }
        // Otherwise, we can just use 100vw.
        return '100vw';
    }
    function generateSrcSet(includedSrcSet, image, width, height, ratio) {
        let srcSetEnabled = includedSrcSet;
        // If difference in widths is minimal, we don't need to generate an explicit srcSet.
        if (width - (mobileWidth || 375) < 100) {
            srcSetEnabled = false;
        }
        // If not excluded, we need to generate a srcSet.
        if (srcSetEnabled) {
            let lowerWidth = 375;
            let stepsRequired = 3;
            let heightRatio = height ? height / width : undefined;
            // If we have a mobile width, we need to set the lower width to that.
            if (mobileWidth) {
                lowerWidth = mobileWidth;
            }
            // If difference in widths is greater than 800, we need to increase the steps required.
            if (width - lowerWidth >= 800) {
                stepsRequired = 5;
            }
            // If we have a ratio, we need to set the height ratio to that.
            if (ratio) {
                heightRatio = ratio;
            }
            const srcSet = getSrcs(image, width, lowerWidth, stepsRequired, heightRatio);
            // If we have a srcSet, then we can use that.
            if (srcSet) {
                return srcSet.join(', ');
            }
        }
        // If we don't have a srcSet, we need to generate a 2x version as a fallback.
        return `${imageUrl(image, {
            width: width ? width * 2 : undefined,
            height: height ? height * 2 : undefined,
        })} 2x`;
    }
};
export default Image;
